import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import IconAlert from '../IconAlert/IconAlert';
import IconClose from '../IconClose/IconClose';
import css from './Toast.module.css';

/**
 * Toast component for displaying temporary messages
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class
 * @param {string} [props.rootClassName] - Custom class that overrides the default class
 * @param {boolean} props.show - Whether the toast is visible
 * @param {string} props.message - The message to display
 * @param {number} [props.duration] - Duration in milliseconds before auto-hiding (default: 5000)
 * @param {Function} [props.onClose] - Callback when toast is closed
 * @returns {JSX.Element} Toast component
 */
const Toast = props => {
  const { className, rootClassName, show, message, duration = 5000, onClose } = props;
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      // Small delay to trigger animation
      setTimeout(() => setIsAnimating(true), 10);
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          if (onClose) {
            onClose();
          }
        }, 300); // Wait for fade-out animation
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setIsAnimating(false);
    }
  }, [show, duration, onClose]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    }, 300);
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.show]: isAnimating,
  });

  const toastContent = isVisible ? (
    <div className={classes}>
      <div className={css.content}>
        <div className={css.iconWrapper}>
          <IconAlert className={css.icon} />
        </div>
        <div className={css.messageWrapper}>
          <p className={css.message}>{message}</p>
        </div>
        <button className={css.closeButton} onClick={handleClose} aria-label="Close">
          <IconClose size="small" className={css.closeIcon} />
        </button>
      </div>
    </div>
  ) : null;

  // Render toast in a portal to avoid stacking context issues
  if (typeof window !== 'undefined') {
    return ReactDOM.createPortal(toastContent, document.body);
  }

  return null;
};

export default Toast;

